import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { NavLink as RouterLink } from 'react-router-dom';
import { Box, CircularProgress, Typography, Divider, List, ListItem, ListItemButton, ListItemAvatar, Avatar, ListItemText } from '@mui/material';

import * as fileApi from '../../lib/api/file';

const UserList = ({
    deptId,
    employees,
    loading,
}) => {
    // 1. Hooks 선언 (최상단 배치)
    const { user } = useSelector(({ auth }) => ({
        user: auth.user,
    }));

    const [profileImgs, setProfileImgs] = useState({});

    // 권한 판별 변수 계산
    const isAdmin = user?.adminRole === 'Y';
    const isLeader = employees?.some(emp => 
        emp.dptUuid === user?.dptInf?.dptUuid && 
        user?.usrUuid === user?.dptInf?.leaderUuid
    );

    // [에러 해결 포인트] useMemo를 loading 조건문보다 위로 이동
    const myInfo = useMemo(() => {
        if (isAdmin) return employees || [];
        if (isLeader) {
            return employees?.filter(emp => emp.dptUuid === user?.dptInf?.dptUuid) || [];
        }
        return employees?.filter(emp => emp.usrUuid === user?.usrUuid) || [];
    }, [employees, user, isAdmin, isLeader]);

    // 프로필 이미지 로드 로직
    useEffect(() => {    
        const employeesWithImg = employees?.filter(emp => emp.profileImg) || [];
        
        const loadAllImages = async () => {
            const newImages = {};
            await Promise.all(employeesWithImg.map(async (emp) => {
                try {
                    const url = await fileApi.profileImage(emp.profileImg);
                    newImages[emp.usrUuid] = url;
                } catch (error) {
                    console.error(`${emp.usrNm} 이미지 로드 실패:`, error);
                }
            }));
            setProfileImgs(prev => ({ ...prev, ...newImages }));
        };

        if (employeesWithImg.length > 0) {
            loadAllImages();
        }

        return () => {
            Object.values(profileImgs).forEach(url => URL.revokeObjectURL(url));
        };
    }, [employees]);

    // 2. Early Return (Hooks 선언 이후에 위치해야 함)
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
                <CircularProgress />
            </Box>
        );
    }

    // 3. JSX 렌더링
    return (
        <Box>
            {employees === null || employees?.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
                    <Typography variant="subtitle2">데이터가 없습니다.</Typography>
                </Box>
            ) : (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', p: 1 }}>
                        <Typography variant="subtitle2">
                            총: {myInfo.length} 명 (담당 부서원만 표시됩니다)
                        </Typography>
                    </Box>
                    <Divider />
                    <List sx={{ width: '100%', height: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                        {myInfo.map((employee, index) => (
                            <ListItem key={employee.usrUuid || index} dense divider={false}>
                                <ListItemButton
                                    component={RouterLink}
                                    to={`/goalsetting/${deptId}/${employee.usrUuid}`}
                                    end
                                    style={({ isActive }) => ({ 
                                        background: isActive ? '#1769aa' : '', 
                                        color: isActive ? '#fff' : '' 
                                    })}
                                >
                                    <ListItemAvatar>
                                        {employee.profileImg ? (
                                            <Avatar alt={employee.usrNm} src={profileImgs[employee.usrUuid]} />
                                        ) : (
                                            <Avatar>{employee?.usrNm?.substring(0, 1)}</Avatar>
                                        )}
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>
                                                {employee.usrNm} {employee.gradeNm} ({employee.dptNm})
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography variant="body2" component="div">
                                                {employee.emlAddr}
                                            </Typography>
                                        }
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}
        </Box>
    );
};

export default UserList;
